import React, { memo } from 'react'
import { Control, FieldErrors } from 'react-hook-form/dist/types'
import { ControlledTextField, ControlledTextInput } from 'transfix-form/components'
import { Box, Grid, makeStyles, Typography } from 'transfix-ui/core'
import { theme } from 'transfix-ui/theme'
import { getErrorMessage } from 'apps/edi-tools/utils'

interface IFtpConnectionConfigGridProps {
  includeFilenameGlob: boolean
  control?: Control
  errors?: FieldErrors
  fieldsRequired?: boolean
}

const useFtpConnectionConfigGridStyles = makeStyles({
  container: {
    marginTop: theme.spacing(0.5),
    marginBottom: theme.spacing(0.5),
  },
  text: {
    whiteSpace: 'pre-line',
  },
  gridItem: {
    display: 'flex',
    alignItems: 'center',
  },
})

export const FtpConnectionConfigGrid = memo<IFtpConnectionConfigGridProps>(
  ({ control, errors, fieldsRequired, includeFilenameGlob }) => {
    const classes = useFtpConnectionConfigGridStyles()

    if (!errors) {
      return null
    }

    return (
      <div>
        <Box mt={2} mb={2}>
          <Typography variant='h3'>Connection Details</Typography>
        </Box>
        <Grid
          container
          justifyContent='flex-start'
          direction='column'
          spacing={1}
          className={classes.container}>
          <Grid item className={classes.gridItem}>
            <ControlledTextField
              control={control}
              name='host'
              TextInputProps={{
                label: 'Host',
                dataTest: 'host-name-field',
                error: !!getErrorMessage(errors, 'host'),
                validationMessage: getErrorMessage(errors, 'host'),
                required: fieldsRequired,
              }}
            />
          </Grid>
          <Grid item className={classes.gridItem}>
            <ControlledTextField
              control={control}
              name='port'
              TextInputProps={{
                icon: 'numberIcon',
                type: 'number',
                label: 'Port',
                dataTest: 'port-number-field',
                error: !!getErrorMessage(errors, 'port'),
                validationMessage: getErrorMessage(errors, 'port'),
                required: fieldsRequired,
              }}
            />
          </Grid>
          <Grid item className={classes.gridItem}>
            <ControlledTextField
              control={control}
              name='username'
              TextInputProps={{
                label: 'Username',
                dataTest: 'username-name-field',
                error: !!getErrorMessage(errors, 'username'),
                validationMessage: getErrorMessage(errors, 'username'),
                required: fieldsRequired,
              }}
            />
          </Grid>
          <Grid item className={classes.gridItem}>
            <ControlledTextField
              control={control}
              name='secretKey'
              TextInputProps={{
                label: 'Secret Key (ENV var or SecretsManager id)',
                dataTest: 'secret-key-field',
                error: !!getErrorMessage(errors, 'secretKey'),
                validationMessage: getErrorMessage(errors, 'secretKey'),
                required: fieldsRequired,
              }}
            />
          </Grid>
          <Grid item className={classes.gridItem}>
            <ControlledTextField
              control={control}
              name='pathPrefix'
              TextInputProps={{
                label: 'Path Prefix',
                dataTest: 'path-prefix-field',
                error: !!getErrorMessage(errors, 'pathPrefix'),
                validationMessage: getErrorMessage(errors, 'pathPrefix'),
                required: fieldsRequired,
              }}
            />
          </Grid>
          {includeFilenameGlob && (
            <Grid item className={classes.gridItem}>
              <ControlledTextInput
                control={control}
                name='filenameGlob'
                TextInputProps={{
                  label: 'Filename GLOB',
                  dataTest: 'filename-glob-field',
                  error: !!getErrorMessage(errors, 'filenameGlob'),
                  validationMessage: getErrorMessage(errors, 'filenameGlob'),
                }}
              />
            </Grid>
          )}
        </Grid>
      </div>
    )
  }
)
